import { Component, ViewChild, effect, inject, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  AccordionModule,
  AccordionComponent,
  AccordionGroupComponent,
} from "@vg-constellation/angular-17/accordion";
import { DrawerComponent, DrawerModule } from "@vg-constellation/angular-17/drawer";
import { TabsComponent, TabsModule } from "@vg-constellation/angular-17/tabs";
import { HelpDrawerTabContentComponent } from "@components/help-drawer/help-drawer-tab-content/help-drawer-tab-content.component";

import { GlobalStateStore } from "@services/global-state/global-state.service";
import { AccountType } from "../../types/AccountType";
import { AccountOwnerType } from "../../types/AccountOwnerType";
import { GUIDED_FLOW_COMPONENT_STEPS } from "@models/guided-flow.map";
import * as helpDrawerContent from "@services/content/help-drawer-content.json";

@Component({
  selector: "app-help-drawer",
  standalone: true,
  imports: [
    CommonModule,
    AccordionModule,
    AccordionComponent,
    AccordionGroupComponent,
    DrawerComponent,
    DrawerModule,
    TabsComponent,
    TabsModule,
    HelpDrawerTabContentComponent,
  ],
  templateUrl: "./help-drawer.component.html",
  styleUrl: "./help-drawer.component.scss",
})
export class HelpDrawerComponent implements OnInit {
  readonly globalStore = inject(GlobalStateStore);

  // Use existing enums to map things to their related content in the help-drawer-content.json
  private readonly accountTypeContentMap = {
    [AccountType.BANK]: "bank",
    [AccountType.RETIREMENT_ANNUITY]: "retirement",
    [AccountType.INVESTMENT]: "investment",
    [AccountType.TRUST]: "trust",
    [AccountType.REAL_ESTATE]: "realEstate",
    [AccountType.LIABILITIES]: "liability",
  };

  private readonly ownerTypeContentMap = {
    [AccountOwnerType.INDIVIDUAL_BY_NAME]: "individual",
    [AccountOwnerType.TRUST]: "trust",
    // TODO: HEYHEY since we currently only have individuals & trusts as owners implemented for now, commenting out business & charity.
    // TODO: Uncomment when we have the related implementation ready; help drawer content is already set up to be used out of the box
    // [AccountOwnerType.BUSINESS]: "business",
    // [AccountOwnerType.CHARITY]: "charity",
  };

  @ViewChild("helpDrawer") helpDrawer!: DrawerComponent;
  helpDrawerContent = helpDrawerContent;

  currentAccountType: string = "";
  // Content for Asset and Ownership tabs to be passed into the help-drawer-tab-content child component through tabContent @Input
  assetTabContent: any = {};
  ownershipTabContentTypes: object[] = [];
  beneficiariesTabContent: any = {};
  showBeneficiariesTab: boolean = false;
  tabActiveItem: string = "ownership";
  selectedOwnerType: string = "";
  currentStep: number = 1;

  constructor() {
    effect(() => {
      // Whenever the currentStep value changes, this effect will trigger to update the tabActiveItem value
      this.currentStep = this.globalStore.getCurrentStep(this.currentAccountType);
      this.setTabsActiveItem();

      // Whenever a user selects a different ownership option on the page, call setSelectedOwnerType
      this.setSelectedOwnerType();
    });
  }

  ngOnInit(): void {
    this.currentAccountType = this.globalStore.getGuidedFlowState("account-type").accountType;
    this.currentStep = this.globalStore.getCurrentStep(this.currentAccountType);

    this.setSelectedOwnerType();
    this.setTabsContent();
    this.setTabsActiveItem();
  }

  setTabsContent(): void {
    this.setAssetTabContent();
    this.setOwnershipTypeHeaders();
    this.setBeneficiariesTabContent();
  }

  setAssetTabContent(): void {
    const assetTabAccountType = this.accountTypeContentMap[this.currentAccountType];

    this.assetTabContent = this.helpDrawerContent.allContent[assetTabAccountType];
  }

  setOwnershipTypeHeaders(): void {
    // If currentAccountType is retirement, only show individual ownership content
    if (this.currentAccountType === AccountType.RETIREMENT_ANNUITY) {
      const capitalizedOwnerTypeHeader =
        "Individual" + this.helpDrawerContent.ownershipTabAccordionIndividualHeader;
      const parsedOwnerTabContent = this.setOwnershipTabContent(
        AccountOwnerType.INDIVIDUAL_BY_NAME,
      );

      const ownershipObject = {
        ownerType: AccountOwnerType.INDIVIDUAL_BY_NAME,
        ownershipLabelText: capitalizedOwnerTypeHeader,
        ownerTabContent: parsedOwnerTabContent,
      };

      this.ownershipTabContentTypes.push(ownershipObject);
      return;
    }

    // For all other account types, show all available ownership types (currently for us that's individuals and trusts as owners)
    for (const ownerType in this.ownerTypeContentMap) {
      const ownerTypeHeader = this.ownerTypeContentMap[ownerType];
      let capitalizedOwnerTypeHeader =
        ownerTypeHeader.charAt(0).toUpperCase() + ownerTypeHeader.slice(1).toLowerCase();

      let parsedOwnerTabContent = {};

      if (ownerTypeHeader === "individual") {
        parsedOwnerTabContent = this.setOwnershipTabContent(AccountOwnerType.INDIVIDUAL_BY_NAME);
        capitalizedOwnerTypeHeader += this.helpDrawerContent.ownershipTabAccordionIndividualHeader;
      } else {
        parsedOwnerTabContent = this.setOwnershipTabContent(ownerTypeHeader);
        capitalizedOwnerTypeHeader += this.helpDrawerContent.ownershipTabAccordionHeader;
      }

      const ownershipObject = {
        ownerType: ownerType,
        ownershipLabelText: capitalizedOwnerTypeHeader,
        ownerTabContent: parsedOwnerTabContent,
      };

      this.ownershipTabContentTypes.push(ownershipObject);
    }
  }

  setOwnershipTabContent(ownerType: string): any {
    const ownershipTabAccountType = this.ownerTypeContentMap[ownerType];
    const rawOwnershipTabContent = this.helpDrawerContent.allContent[ownershipTabAccountType];

    // Replace the placeholder content based on the currentAccountType
    return this.replacePlaceholderContent(rawOwnershipTabContent, this.currentAccountType);
  }
  /**
   * In the help-drawer-content.json -> individualByName section, there are {{accountType}} and {{entity}} placeholders that need to be replaced based on the current account type
   * Takes in the raw json content with the placeholders and the current account type
   * Returns the content with the placeholders replaced
   * The two current special cases are real estate and liabilities
   * Everything else currently defaults to "account" for both placeholders
   **/
  replacePlaceholderContent(content: any, accountType: string): any {
    // Don't know if we need this guard clause since we're handling all inputs but just in case I guess
    if (!content || !accountType) {
      return content;
    }

    // Default replacement values for placeholders
    let accountTypeReplacement = "account";
    let entityReplacement = "account";

    // Update replacement values for the special case account types (currently real estate and liabilities)
    if (accountType === AccountType.REAL_ESTATE) {
      accountTypeReplacement = "real estate";
      entityReplacement = "property";
    } else if (accountType === AccountType.LIABILITIES) {
      accountTypeReplacement = "liability";
      entityReplacement = "liability";
    }

    // Convert to JSON string, replace placeholders, parse back
    const jsonString = JSON.stringify(content);
    const replacedString = jsonString
      .replace(/\{\{accountType\}\}/g, accountTypeReplacement)
      .replace(/\{\{entity\}\}/g, entityReplacement);

    return JSON.parse(replacedString);
  }

  setSelectedOwnerType(): void {
    const guidedFlowState = this.globalStore.getGuidedFlowState(this.currentAccountType);
    const guidedFlowTempState = this.globalStore.getGuidedFlowTempState(this.currentAccountType);

    // In both cases (edit or add), prioritize tempState to check if the user has made a selection
    if (guidedFlowState.isEdit) {
      this.selectedOwnerType =
        guidedFlowTempState.selectedAccountOwnerType || guidedFlowState.selectedAccountOwnerType;
    } else {
      this.selectedOwnerType = guidedFlowTempState.selectedAccountOwnerType;
    }
  }

  // Function expands the selected ownership type accordion
  isOwnershipExpanded(ownerType: string): boolean {
    return ownerType === this.selectedOwnerType;
  }

  setTabsActiveItem(): void {
    if (this.currentStep === 1) {
      this.tabActiveItem = "ownership";
      // This will currently just be 2 since we only have 2 steps for each accountType iteration; just slightly future proofs our code so we just need to update the guided-flow.map.ts file if we add more steps later
    } else if (this.currentStep === GUIDED_FLOW_COMPONENT_STEPS[this.currentAccountType].length) {
      this.tabActiveItem = "asset";
    }
  }

  setBeneficiariesTabContent(): void {
    const podEligibleTypes = [
      AccountType.BANK,
      AccountType.RETIREMENT_ANNUITY,
      AccountType.INVESTMENT,
    ];
    this.showBeneficiariesTab = podEligibleTypes.includes(this.currentAccountType as AccountType);

    if (this.showBeneficiariesTab) {
      this.beneficiariesTabContent = this.helpDrawerContent.allContent["beneficiaries"];
    }
  }

  handleDrawerState(activeTab?: string): void {
    if (activeTab) {
      this.tabActiveItem = activeTab;
    }
    if (this.helpDrawer) {
      if (this.helpDrawer.isOpen) {
        this.helpDrawer.closeDrawer();
      } else {
        this.helpDrawer.openDrawer();
      }
    }
  }
}
